import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';
import { products } from '../data/products';

const values = [
  {
    number: '01',
    title: 'Nature First',
    body: 'Every ingredient is sourced from the earth\'s most potent ecosystems. We don\'t synthesize what nature already perfected over millennia.',
  },
  {
    number: '02',
    title: 'Science Backed',
    body: 'Traditional knowledge is our starting point, not our endpoint. Every compound we use is validated against peer-reviewed research.',
  },
  {
    number: '03',
    title: 'Human Centered',
    body: 'Wellness is not one-size-fits-all. We formulate for real lives — high-output days, recovery nights, everything in between.',
  },
  {
    number: '04',
    title: 'Uncompromising Quality',
    body: 'Third-party tested, GMP certified, standardized extracts. No shortcuts from cultivation to capsule — ever.',
  },
];

const timeline = [
  { year: '2019', title: 'The Beginning', body: 'Founded with a simple conviction: the gap between ancient botanical wisdom and modern performance was bridgeable.' },
  { year: '2020', title: 'Research & Development', body: 'Partnered with leading mycologists and herbalists to develop extraction methods that preserve full-spectrum potency.' },
  { year: '2021', title: 'First Products', body: 'Launched RUSH, SYNAPTIQ, and VITRA after two years of formulation, testing, and refinement.' },
  { year: '2023', title: 'Community Growth', body: 'Built a global community of 12,000+ optimizers — athletes, founders, creatives — who share our commitment to natural performance.' },
  { year: '2024', title: 'Innovation Continues', body: 'Expanding research into novel adaptogenic compounds and bioavailability systems for the next generation of formulas.' },
];

export function AboutPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-6 md:pb-8" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-end">

            {/* Text */}
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 32 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="pb-6 md:pb-16"
            >
              <p className="text-xs font-medium tracking-[0.24em] md:tracking-[0.3em] uppercase mb-5 md:mb-8" style={{ color: '#B8704D' }}>
                About Berleto
              </p>
              <h1 className="text-[clamp(2.6rem,14vw,3.4rem)] md:text-6xl font-light leading-[0.94] md:leading-tight mb-6 md:mb-8" style={{ color: '#1A2A1A' }}>
                Elevated wellness
                <br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>through nature's intelligence.</span>
              </h1>
              <p className="text-[15px] md:text-base leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '44ch' }}>
                We started with a question: what happens when you take botanical compounds that have been used for thousands of years and apply modern extraction science to them? Berleto is the answer.
              </p>
            </motion.div>

            {/* Product bottles — side by side */}
            <motion.div
              initial={{ opacity: 0, x: 32 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-end justify-center gap-[clamp(0.85rem,4vw,1.5rem)] pt-4 md:pt-8 pb-5 md:pb-8 overflow-hidden"
            >
              {[
                { product: products.rush, width: 'clamp(72px, 24vw, 110px)' },
                { product: products.vitra, width: 'clamp(102px, 32vw, 160px)' },
                { product: products.synaptiq, width: 'clamp(72px, 24vw, 110px)' },
              ].map(({ product: p, width }) => (
                <motion.div
                  key={p.id}
                  className="relative flex flex-col items-center flex-none cursor-pointer"
                  style={{ width }}
                  initial="rest"
                  whileHover="hover"
                  animate="rest"
                  onClick={() => navigate(`/products/${p.id}`)}
                >
                  <motion.img
                    src={p.image}
                    alt={p.title}
                    className="object-contain drop-shadow-xl w-full"
                    variants={{ rest: { y: 0 }, hover: { y: -6 } }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.span
                    variants={{ rest: { opacity: 0, y: 4 }, hover: { opacity: 1, y: 0 } }}
                    transition={{ duration: 0.2 }}
                    className="absolute -bottom-6 text-xs tracking-[0.2em] uppercase pointer-events-none"
                    style={{ color: '#B8704D' }}
                  >
                    Shop Now
                  </motion.span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Stat strip */}
          <div className="mt-0 pt-5 md:pt-8 border-t grid grid-cols-3" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {[
              { n: '2019', l: 'Founded' },
              { n: '12k+', l: 'Community members' },
              { n: '3', l: 'Flagship products' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={heroInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="px-2 sm:px-4 md:px-8 py-3 md:py-4 border-r last:border-r-0 text-center md:text-left"
                style={{ borderColor: 'rgba(26,42,26,0.08)' }}
              >
                <p className="text-[clamp(1.35rem,7vw,2rem)] md:text-2xl font-light mb-1" style={{ color: '#1A2A1A' }}>{s.n}</p>
                <p className="text-[10px] sm:text-xs tracking-[0.16em] md:tracking-widest uppercase leading-snug" style={{ color: 'rgba(26,42,26,0.4)' }}>{s.l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-24" style={{ background: '#E8DCC4' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl mx-auto"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-10" style={{ color: '#B8704D' }}>
              Our Mission
            </p>
            <p className="text-2xl md:text-3xl font-light leading-relaxed" style={{ color: '#1A2A1A' }}>
              To elevate human potential through nature's most powerful adaptogens — not by replacing what modern life demands of you, but by making you more capable of meeting it.
            </p>
            <div className="mt-12 h-px w-12" style={{ background: '#B8704D' }} />
            <p className="mt-8 text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.6)', maxWidth: '52ch' }}>
              Every product is a bridge between ancient wisdom and modern science. We don't believe in wellness theater — no unnecessary fillers, no marketing claims unsupported by evidence, no compromises on what goes in the capsule.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="md:sticky md:top-32"
            >
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                What We Stand For
              </p>
              <h2 className="text-4xl md:text-5xl font-light leading-tight" style={{ color: '#1A2A1A' }}>
                Four principles.<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>No exceptions.</span>
              </h2>
            </motion.div>

            <div>
              {values.map((v, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-6 py-7 border-b"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <span className="text-xs font-medium tracking-widest mt-0.5 flex-shrink-0 w-6" style={{ color: '#B8704D' }}>
                    {v.number}
                  </span>
                  <div>
                    <h3 className="text-base font-medium mb-2" style={{ color: '#1A2A1A' }}>{v.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)' }}>{v.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24" style={{ background: '#1A2A1A' }} data-bubble-theme="light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ color: '#B8704D' }}>
              Our Journey
            </p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: '#FAF9F6' }}>
              From idea to impact.
            </h2>
          </motion.div>

          <div className="max-w-2xl space-y-0">
            {timeline.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="flex gap-8 py-8 border-b"
                style={{ borderColor: 'rgba(139,154,139,0.15)' }}
              >
                <span className="text-xs font-medium tracking-widest flex-shrink-0 w-10 mt-0.5" style={{ color: '#B8704D' }}>
                  {item.year}
                </span>
                <div>
                  <h3 className="text-base font-medium mb-2" style={{ color: '#FAF9F6' }}>{item.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(139,154,139,0.65)' }}>{item.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-xl"
          >
            <p className="text-xs font-medium tracking-[0.25em] uppercase mb-6" style={{ color: '#B8704D' }}>
              Join Us
            </p>
            <h2 className="text-4xl md:text-5xl font-light mb-4" style={{ color: '#1A2A1A' }}>
              The range is ready.<br />
              <span style={{ color: 'rgba(26,42,26,0.28)' }}>Are you?</span>
            </h2>
            <p className="text-sm mb-10 leading-relaxed" style={{ color: 'rgba(26,42,26,0.5)' }}>
              Three formulas. One standard. Start where it makes sense for you.
            </p>
            <button
              onClick={() => navigate('/products')}
              className="px-10 py-4 text-sm font-medium tracking-wide transition-colors"
              style={{ background: '#1A2A1A', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#243424'}
              onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
            >
              Explore Products
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
